import React, { useCallback } from 'react';
import { Component } from '@/types';
import { GanttChart } from '@/components/pm/GanttChart';
import { useInteractionContext } from './InteractionContextRuntime';
import { useComponentDataResolver } from '@/services/component-data/useComponentDataResolver';
import type { ComponentDataResolverResult } from '@/services/component-data/componentDataResolver';

export interface SharedGanttChartComponentProps {
  component: Component;
  isViewerMode?: boolean;
  inheritedDataResolver?: ComponentDataResolverResult;
}

interface SharedGanttChartResolvedProps {
  component: Component;
  isViewerMode: boolean;
  dataResolver: ComponentDataResolverResult;
}

const SharedGanttChartResolved: React.FC<SharedGanttChartResolvedProps> = ({
  component,
  isViewerMode,
  dataResolver,
}) => {
  const interaction = useInteractionContext();

  const handleAddTask = useCallback(() => {
    interaction.handlers.onAddGanttTask?.(component.id);
  }, [interaction, component.id]);

  const handleEditTask = useCallback((taskId: string, taskData: Record<string, unknown>) => {
    interaction.handlers.onEditGanttTask?.(component.id, taskId, taskData);
  }, [interaction, component.id]);

  // Loading state
  if (dataResolver.loading && dataResolver.rows.length === 0) {
    return (
      <div
        data-testid="gantt-loading"
        className="w-full h-full flex items-center justify-center gap-2 text-muted-foreground text-sm"
      >
        <svg
          className="animate-spin"
          style={{ width: 20, height: 20 }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M21 12a9 9 0 11-6.219-8.56" />
        </svg>
        Loading Gantt chart data...
      </div>
    );
  }

  // Error state
  if (dataResolver.error && dataResolver.rows.length === 0) {
    return (
      <div
        data-testid="gantt-error"
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
        }}
      >
        <span className="text-sm text-destructive">{dataResolver.error.message}</span>
        <button
          onClick={() => dataResolver.refetch()}
          style={{
            fontSize: 12,
            color: '#3b82f6',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <GanttChart
      component={component}
      data={dataResolver.rows}
      isViewerMode={isViewerMode}
      writeTargetPermissions={dataResolver.writeTarget.operations}
      writeTargetRequestingOrganizationId={
        dataResolver.writeTarget.requestingOrganizationId
      }
      onAddTask={isViewerMode ? handleAddTask : undefined}
      onEditTask={isViewerMode ? handleEditTask : undefined}
    />
  );
};

const SharedGanttChartStandalone: React.FC<
  Pick<SharedGanttChartComponentProps, 'component' | 'isViewerMode'>
> = ({ component, isViewerMode = false }) => {
  const dataResolver = useComponentDataResolver(component);
  return (
    <SharedGanttChartResolved
      component={component}
      isViewerMode={isViewerMode}
      dataResolver={dataResolver}
    />
  );
};

export const SharedGanttChartComponent: React.FC<SharedGanttChartComponentProps> = ({
  component,
  isViewerMode = false,
  inheritedDataResolver,
}) => {
  if (inheritedDataResolver !== undefined) {
    return (
      <SharedGanttChartResolved
        component={component}
        isViewerMode={isViewerMode}
        dataResolver={inheritedDataResolver}
      />
    );
  }

  return (
    <SharedGanttChartStandalone
      component={component}
      isViewerMode={isViewerMode}
    />
  );
};

SharedGanttChartComponent.displayName = 'SharedGanttChartComponent';
